import { computed, ref } from 'vue';

/** Chrome and Edge's install event (not in the DOM typings yet). */
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const deferred = ref<InstallPromptEvent | null>(null);
const installed = ref(false);

/**
 * Called once at startup: the browser offers installing only once, early, so
 * the offer is kept until someone clicks "Install Dometry".
 */
export function listenForInstall(): void {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferred.value = event as InstallPromptEvent;
  });
  window.addEventListener('appinstalled', () => {
    installed.value = true;
    deferred.value = null;
  });
}

export type InstallHint = 'button' | 'installed' | 'ios' | 'safari-mac' | 'unavailable';

/**
 * Installing Dometry as an app: a button where the browser allows it (Chrome,
 * Edge), instructions where it doesn't (Safari), and nothing to do once it's
 * installed (running in its own window).
 */
export function useInstallPrompt() {
  const standalone = () =>
    installed.value ||
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true;

  const hint = computed<InstallHint>(() => {
    if (standalone()) return 'installed';
    if (deferred.value) return 'button';
    const ua = navigator.userAgent;
    if (/iPhone|iPad/.test(ua)) return 'ios';
    if (/Macintosh/.test(ua) && /Safari\//.test(ua) && !/Chrome|Edg|Firefox/.test(ua))
      return 'safari-mac';
    return 'unavailable';
  });

  async function install(): Promise<boolean> {
    const event = deferred.value;
    if (!event) return false;
    await event.prompt();
    const { outcome } = await event.userChoice;
    deferred.value = null;
    if (outcome === 'accepted') installed.value = true;
    return outcome === 'accepted';
  }

  return { hint, install };
}
